import React, { useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { CanAccess } from '../../Elements/Access'
import TitleContext from '../../../TitleContext'
import RegionFormCard, {
  RegionFormCardVariants,
} from '../../Card/RegionFormCard'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import EmptyRegion from './EmptyRegion'
import {
  Region,
  RegionContractingHierarchy,
  RegionFeeSetting,
} from '../../../swagger'
import { regionsApi } from '../../../api/swagger'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'

const RegionDetails: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const title = t('Regions.AddRegion.Header', 'Regions')
  const { useTitleEffect } = useContext(TitleContext)
  useTitleEffect(title)

  const { handleError } = useAppSnackbar()

  const {
    Regions: { fetchRegion: fetchRegionLoadingId },
  } = useLoadingIds()

  const { regionKey } = useParams<{
    regionKey: string
  }>()

  const [regionDetails, setRegionDetails] = useState<Region>()
  const [regionFees, setRegionFees] = useState<RegionFeeSetting[]>()
  const [regionContractingHierarchies, setRegionContractingHierarchies] =
    useState<RegionContractingHierarchy[]>()

  const fetchRegionDetails = async () => {
    try {
      const requestParam = { regionKey: +`${regionKey}` }

      /** Ensure all 3 endpoints that feed into region details are successful */
      const [
        fetchedRegionDetails,
        fetchedRegionFees,
        fetchedRegionContractingHierarchies,
      ] = await Promise.all([
        regionsApi.fetchRegion(requestParam),
        regionsApi.fetchRegionFees(requestParam),
        regionsApi.fetchRegionContractingHierarchies(requestParam),
      ])

      setRegionDetails(fetchedRegionDetails)
      setRegionFees(fetchedRegionFees)
      setRegionContractingHierarchies(fetchedRegionContractingHierarchies)
    } catch (err) {
      await handleError(
        err,
        t(
          'Regions.FetchRegionDetails.FailedToFetch',
          'Failed to fetch region details.'
        )
      )
    }
  }

  // identifying this as 'fetchRegion' even though it calls 3 endpoints, will probably be updated with a convention for functions alike
  const { triggerFetch, isLoading } = useLoadingContext({
    asyncFunction: fetchRegionDetails,
    loadingId: fetchRegionLoadingId,
  })

  /**
   * Fetch Region Details
   */
  useEffect(() => {
    if (regionKey !== '') {
      triggerFetch()
    }
  }, [regionKey, triggerFetch])

  // Programmatically trigger a refetch of region details
  const handleSave = triggerFetch

  const navigateToRegions = () => {
    navigate({
      pathname: '/admin/regions',
    })
  }

  const navigateToParentRegion = (regionId?: number) => {
    if (!regionId) return
    navigate({
      pathname: `/admin/regions/region-details/${regionId.toString()}`,
    })
  }

  if (!regionDetails) {
    return (
      <CanAccess I="view" on="Region">
        <EmptyRegion isLoading={isLoading} />
      </CanAccess>
    )
  }

  return (
    <CanAccess I="accessRegionsSection" on="Admin">
      <CanAccess I="view" on="Region">
        <DynamicBreadcrumbs
          breadcrumbs={[
            {
              label: t('Regions.AddRegion.BreadCrumb.Regions', 'Regions'),
              onClick: navigateToRegions,
            },
            {
              label: regionDetails.parentRegionName,
              onClick: () =>
                navigateToParentRegion(regionDetails.parentRegionsKey),
            },
            {
              label: regionDetails.name,
            },
          ]}
        />
        <RegionFormCard
          variant={RegionFormCardVariants.EditRegion}
          regionDetails={regionDetails}
          regionFees={regionFees}
          regionContractingHierarchies={regionContractingHierarchies}
          handleSave={handleSave}
        />
      </CanAccess>
    </CanAccess>
  )
}

export default RegionDetails
